/**
 * Fixtures for the Today day view (Day redesign phase 1): RAW per-project
 * `day` objects in the exact shape GET /api/cockpit/today now returns
 * (azure-functions/cis/shared/cockpit.js, DEC-CHAT-20261013-201), in real yard
 * naming (blocks UD10, BG7, BS2-D, 3D1; places from the yard's daily booking
 * list, tests/fixtures/cis/yard/ss418-daily-booking-*.xlsx). People are roles
 * only: the signed-in site manager and one assistant inspector, both on the
 * SS418 roster of fixtures/plan.js.
 *
 * - fixtureDayFour : the 4-inspection day of the phone artboards (5 sides)
 * - fixtureDayBusy : the desktop TodayBusy artboard's day (18 lines, 27 sides)
 * - makeDayFixture : a generated day of N lines (the 20-inspection test day)
 * - fixtureDayEmpty: no list yet, one carried item, about 3 expected
 */

"use strict";

export const FIXTURE_DAY_DATE = "2026-10-27"; // Tue
export const FIXTURE_DAY_ME = "t.aoki@example.com"; // the signed-in site manager (a lane on SS418)
export const FIXTURE_DAY_ASSISTANT = "k.sato@example.com"; // the assistant inspector (bare roster entry)

const SM = FIXTURE_DAY_ME;
const AS = FIXTURE_DAY_ASSISTANT;
const DASH = "–";

let seq = 0;
/** One line. `sides`: [side, state, assignedTo, result?] ; defects: [[text, date], ...]. */
function line(time, block, title, place, sides, defects, extra = {}) {
  seq += 1;
  const [start] = time.split(DASH);
  return {
    key: `${block}|ss418|${title}|${start}`,
    plannedTime: start,
    timeSlot: time,
    title,
    code: extra.stageCode || (title.includes("steel cutting") ? "S/C" : "CH"), // the yard STAGE code, never the project
    projectCode: "SS418",
    category: extra.category || "Hull Erection",
    inspectionType: "Witness Point",
    stage: extra.stage || "Daily booking",
    witnessParties: "",
    notes: "",
    place,
    block,
    sides: sides.map(([side, state, assignedTo, result], i) => ({
      side,
      scheduleRef: `DB-20261027-${String(seq).padStart(4, "0")}${side ? `-${side}` : ""}`,
      etag: '"1"',
      assignedTo: assignedTo || null,
      state,
      ...(result ? { result } : {}),
      ...(state === "recorded" ? { inspectionNumber: 1000 + seq * 2 + i } : {}),
    })),
    repairCheck: defects && defects.length ? { openDefects: defects.map(([text, inspectionDate]) => ({ text, inspectionDate })) } : null,
  };
}

export function countsOf(lines, unscheduled, postponed = 0) {
  const sides = lines.flatMap((l) => l.sides);
  const recorded = sides.filter((s) => s.state === "recorded").length;
  const post = sides.filter((s) => s.state === "postponed").length || postponed;
  const carriedOver = sides.filter((s) => s.state === "carried").length;
  const notAttended = sides.filter((s) => s.state === "not_attended").length;
  return { requested: sides.length, recorded, toGo: sides.length - recorded - post - carriedOver - notAttended, postponed: post, unscheduled, carriedOver, notAttended };
}

function dayObject(lines, { unscheduled = 0, carried = [], importFile = "2026.10.27.xlsx", expectedCount, toApprove = [], approvedCount = 0 } = {}) {
  return {
    date: FIXTURE_DAY_DATE,
    lines,
    counts: countsOf(lines, unscheduled),
    toApprove,
    approvedCount,
    carried,
    lastDailyImport: importFile ? { at: "2026-10-27T07:42:00Z", localTime: "07:42", file: importFile } : null,
    ...(expectedCount !== undefined ? { expectedCount } : {}),
  };
}

export function buildFixtureDayFour() {
  seq = 0;
  const lines = [
    line("08:30–09:00", "UD10", "block final inspection", "二期 assembly shop, bay 4", [["P", "recorded", SM, "Accepted"], ["S", "recorded", SM, "Accepted with comments"]]),
    line("10:00–10:30", "BG7", "welding inspection", "四期 assembly shop, bay 2", [["P", "to_go", null]], [["Undercut at frame 112", "2026-10-20"], ["spatter on face plate", "2026-10-20"]]),
    line("13:30–14:00", "BS2-D", "steel cutting", "四期 NC workshop (FAB13), NC machine 3", [["", "to_go", null]]),
    line("14:30–15:00", "3D1", "block final inspection", "二期 assembly shop, bay 6", [["S", "to_go", AS]]),
  ];
  return dayObject(lines, { unscheduled: 1 });
}

export function buildFixtureDayBusy() {
  seq = 100;
  const T = (a, b) => `${a}–${b}`;
  const lines = [
    // not assigned yet
    line(T("11:30", "12:00"), "BG9", "welding inspection", "四期 assembly shop, bay 3", [["P", "to_go", null], ["S", "to_go", null]], [["Porosity at bracket weld", "2026-10-22"]]),
    line(T("15:30", "16:00"), "BS4-D", "steel cutting", "四期 NC workshop (FAB13), NC machine 1", [["", "to_go", null]]),
    line(T("16:00", "16:30"), "UD14", "block final inspection", "二期 assembly shop, bay 2", [["P", "to_go", null]]),
    // site manager
    line(T("08:00", "08:30"), "UD10", "block final inspection", "二期 assembly shop, bay 4", [["P", "recorded", SM, "Accepted"], ["S", "recorded", SM, "Accepted"]]),
    line(T("08:30", "09:00"), "UD11", "block final inspection", "二期 assembly shop, bay 5", [["P", "recorded", SM, "Accepted"], ["S", "recorded", SM, "Accepted"]]),
    line(T("09:30", "10:00"), "BG7", "welding inspection", "四期 assembly shop, bay 2", [["P", "recorded", SM, "Accepted"]], [["Undercut at frame 112", "2026-10-20"], ["spatter on face plate", "2026-10-20"]]),
    line(T("10:00", "10:30"), "3D1", "block final inspection", "二期 assembly shop, bay 6", [["P", "recorded", SM, "Accepted"], ["S", "recorded", SM, "Accepted"]]),
    line(T("11:00", "11:30"), "3D2", "block final inspection", "二期 assembly shop, bay 6", [["P", "to_go", SM], ["S", "to_go", SM]]),
    line(T("13:00", "13:30"), "BG5", "welding inspection", "四期 assembly shop, bay 1", [["S", "to_go", SM]]),
    line(T("14:00", "14:30"), "UD12", "block final inspection", "二期 assembly shop, bay 4", [["P", "to_go", SM], ["S", "to_go", SM]]),
    line(T("15:00", "15:30"), "BS2-D", "steel cutting", "四期 NC workshop (FAB13), NC machine 3", [["", "to_go", SM]]),
    // assistant inspector
    line(T("08:00", "08:30"), "BG3", "welding inspection", "四期 assembly shop, bay 1", [["P", "recorded", AS, "Accepted"], ["S", "recorded", AS, "Accepted"]]),
    line(T("09:00", "09:30"), "2D4", "block final inspection", "二期 assembly shop, bay 3", [["P", "recorded", AS, "Accepted"]]),
    line(T("09:30", "10:00"), "BG4", "welding inspection", "四期 assembly shop, bay 2", [["S", "recorded", AS, "Accepted"]], [["Missing run-off tab", "2026-10-21"], ["Crack at toe, frame 96", "2026-10-21"], ["Slag left on weld", "2026-10-21"]]),
    line(T("10:30", "11:00"), "2D5", "block final inspection", "二期 assembly shop, bay 3", [["P", "to_go", AS], ["S", "to_go", AS]]),
    line(T("13:00", "13:30"), "UD13", "block final inspection", "二期 assembly shop, bay 5", [["S", "to_go", AS]]),
    line(T("13:30", "14:00"), "BG6", "welding inspection", "四期 assembly shop, bay 3", [["P", "to_go", AS], ["S", "to_go", AS]]),
    line(T("14:30", "15:00"), "2D6", "block final inspection", "二期 assembly shop, bay 1", [["P", "to_go", AS]]),
  ];
  return dayObject(lines, { unscheduled: 2 });
}

const BLOCKS = ["UD10", "UD11", "UD12", "UD13", "UD14", "BG3", "BG4", "BG5", "BG6", "BG7", "BG9", "BS2-D", "BS4-D", "3D1", "3D2", "2D4", "2D5", "2D6", "UD15", "BG10"];

/** A generated day of `n` lines (n sides + a second side on every third line), alternating lanes. */
export function makeDayFixture(n = 20) {
  seq = 500;
  const lines = [];
  for (let i = 0; i < n; i += 1) {
    const block = BLOCKS[i % BLOCKS.length];
    const hour = 8 + Math.floor(i / 2);
    const half = i % 2 === 0 ? "00" : "30";
    const next = i % 2 === 0 ? `${String(hour).padStart(2, "0")}:30` : `${String(hour + 1).padStart(2, "0")}:00`;
    const welding = block.startsWith("BG");
    const who = i % 5 === 0 ? null : i % 2 === 0 ? SM : AS;
    const state = i < 6 ? "recorded" : "to_go";
    const sides = i % 3 === 0 ? [["P", state, who, state === "recorded" ? "Accepted" : undefined], ["S", state, who, state === "recorded" ? "Accepted" : undefined]] : [["P", state, who, state === "recorded" ? "Accepted" : undefined]];
    lines.push(line(`${String(hour).padStart(2, "0")}:${half}–${next}`, block, welding ? "welding inspection" : "block final inspection", welding ? "四期 assembly shop, bay 2" : "二期 assembly shop, bay 4", sides, welding && i % 4 === 1 ? [["Undercut at frame 112", "2026-10-20"]] : null));
  }
  return dayObject(lines, { unscheduled: 1 });
}

/**
 * The EndOfDay artboard's day (mockups/cockpit-v3/EndOfDay.dc.html): 27 requested
 * sides, 23 recorded, 3 postponed (BG6 P and S, UD13 S), 1 with no result (2D6 P),
 * and 2 records without a yard request (UD9). 25 records wait for approval.
 */
export function buildFixtureDayEnd() {
  seq = 200;
  const T = (a, b) => `${a}${DASH}${b}`;
  const A = "Accepted";
  const C = "Accepted with comments";
  const N = "Not Accepted";
  const lines = [
    line(T("08:00", "08:30"), "UD10", "block final inspection", "二期 assembly shop, bay 4", [["P", "recorded", SM, A], ["S", "recorded", SM, C]]),
    line(T("08:00", "08:30"), "BG3", "welding inspection", "四期 assembly shop, bay 1", [["P", "recorded", AS, A], ["S", "recorded", AS, A]]),
    line(T("08:30", "09:00"), "UD11", "block final inspection", "二期 assembly shop, bay 5", [["P", "recorded", SM, A], ["S", "recorded", SM, A]]),
    line(T("09:00", "09:30"), "2D4", "block final inspection", "二期 assembly shop, bay 3", [["P", "recorded", AS, C]]),
    line(T("09:30", "10:00"), "BG7", "welding inspection", "四期 assembly shop, bay 2", [["P", "recorded", SM, A]]),
    line(T("09:30", "10:00"), "BG4", "welding inspection", "四期 assembly shop, bay 2", [["S", "recorded", AS, N]]),
    line(T("10:00", "10:30"), "3D1", "block final inspection", "二期 assembly shop, bay 6", [["P", "recorded", SM, A], ["S", "recorded", SM, A]]),
    line(T("10:30", "11:00"), "2D5", "block final inspection", "二期 assembly shop, bay 3", [["P", "recorded", AS, A], ["S", "recorded", AS, C]]),
    line(T("11:00", "11:30"), "3D2", "block final inspection", "二期 assembly shop, bay 6", [["P", "recorded", SM, A], ["S", "recorded", SM, A]]),
    line(T("11:30", "12:00"), "BG9", "welding inspection", "四期 assembly shop, bay 3", [["P", "recorded", SM, A], ["S", "recorded", SM, A]]),
    line(T("13:00", "13:30"), "BG5", "welding inspection", "四期 assembly shop, bay 1", [["S", "recorded", SM, C]]),
    line(T("13:30", "14:00"), "BG6", "welding inspection", "四期 assembly shop, bay 3", [["P", "postponed", AS, "Postponed"], ["S", "postponed", AS, "Postponed"]]),
    line(T("14:00", "14:30"), "UD12", "block final inspection", "二期 assembly shop, bay 4", [["P", "recorded", SM, A], ["S", "recorded", SM, A]]),
    line(T("14:30", "15:00"), "2D6", "block final inspection", "二期 assembly shop, bay 1", [["P", "to_go", AS]]),
    line(T("15:00", "15:30"), "BS2-D", "steel cutting", "四期 NC workshop (FAB13), NC machine 3", [["", "recorded", SM, A]]),
    line(T("15:00", "15:30"), "UD13", "block final inspection", "二期 assembly shop, bay 5", [["S", "postponed", AS, "Postponed"]]),
    line(T("15:30", "16:00"), "BS4-D", "steel cutting", "四期 NC workshop (FAB13), NC machine 1", [["", "recorded", AS, A]]),
    line(T("16:00", "16:30"), "UD14", "block final inspection", "二期 assembly shop, bay 2", [["P", "recorded", AS, A]]),
  ];
  const rec = (n, time, block, title, side, result, defect, photos, by, scheduled = true) => ({
    inspectionNumber: n,
    etag: `"${n}"`,
    time,
    title,
    block,
    side,
    result,
    defectNoted: Boolean(defect),
    photoCount: photos,
    capturedBy: by,
    scheduled,
  });
  const blk = "block final inspection";
  const wld = "welding inspection";
  const toApprove = [
    rec(2001, "08:00", "UD10", blk, "P", A, "", 3, SM),
    rec(2002, "08:00", "UD10", blk, "S", C, "Gap 4 mm at frame 108", 4, SM),
    rec(2003, "08:00", "BG3", wld, "P", A, "", 2, AS),
    rec(2004, "08:00", "BG3", wld, "S", A, "", 2, AS),
    rec(2005, "08:30", "UD11", blk, "P", A, "", 3, SM),
    rec(2006, "08:30", "UD11", blk, "S", A, "", 2, SM),
    rec(2007, "09:00", "2D4", blk, "P", C, "Misaligned stiffener, frame 96", 5, AS),
    rec(2008, "09:30", "BG7", wld, "P", A, "", 4, SM),
    rec(2009, "09:30", "BG4", wld, "S", N, "2 of 3 defects not repaired", 6, AS),
    rec(2010, "10:00", "3D1", blk, "P", A, "", 2, SM),
    rec(2011, "10:00", "3D1", blk, "S", A, "", 0, SM),
    rec(2012, "10:30", "2D5", blk, "P", A, "", 3, AS),
    rec(2013, "10:30", "2D5", blk, "S", C, "Spatter on deck plate", 2, AS),
    rec(2014, "11:00", "3D2", blk, "P", A, "", 3, SM),
    rec(2015, "11:00", "3D2", blk, "S", A, "", 2, SM),
    rec(2016, "11:30", "BG9", wld, "P", A, "", 3, SM),
    rec(2017, "11:30", "BG9", wld, "S", A, "", 3, SM),
    rec(2018, "13:00", "BG5", wld, "S", C, "Sharp edge at manhole", 4, SM),
    rec(2019, "14:00", "UD12", blk, "P", A, "", 3, SM),
    rec(2020, "14:00", "UD12", blk, "S", A, "", 3, SM),
    rec(2021, "15:00", "BS2-D", "steel cutting", "", A, "", 2, SM),
    rec(2022, "15:30", "BS4-D", "steel cutting", "", A, "", 2, AS),
    rec(2023, "16:00", "UD14", blk, "P", A, "", 3, AS),
    rec(2024, "", "UD9", blk, "P", A, "", 2, SM, false),
    rec(2025, "", "UD9", blk, "S", A, "", 2, SM, false),
  ];
  return dayObject(lines, { unscheduled: 2, toApprove });
}

export function buildFixtureDayEmpty() {
  seq = 900;
  const carried = [
    { scheduleRef: "DB-20261026-0900-S", etag: '"1"', title: "welding inspection", code: "CH", projectCode: "SS418", category: "Hull Erection", plannedDate: "2026-10-26", plannedTime: "", place: "", block: "BG3", side: "S", assignedTo: null },
  ];
  return { date: FIXTURE_DAY_DATE, lines: [], counts: { requested: 0, recorded: 0, toGo: 0, postponed: 0, unscheduled: 0, carriedOver: 0, notAttended: 0 }, toApprove: [], approvedCount: 0, carried, lastDailyImport: null, expectedCount: 3 };
}

export const fixtureDayFour = buildFixtureDayFour();
export const fixtureDayBusy = buildFixtureDayBusy();
export const fixtureDayEmpty = buildFixtureDayEmpty();
export const fixtureDayEnd = buildFixtureDayEnd();
export const fixtureDayTwenty = makeDayFixture(20);

const EMPTY_DAY = (date) => ({ date, lines: [], counts: { requested: 0, recorded: 0, toGo: 0, postponed: 0, unscheduled: 0, carriedOver: 0, notAttended: 0 }, toApprove: [], approvedCount: 0, carried: [], lastDailyImport: null });

/** The Today envelope for a fixture world: `main` keeps the multi-project world and gives SS418 the 4-inspection day. */
export function dayEnvelopeFor(world, baseEnvelope) {
  const day = world === "day-busy" ? fixtureDayBusy : world === "day-empty" ? fixtureDayEmpty : world === "day-20" ? fixtureDayTwenty : world === "end-of-day" ? buildFixtureDayEnd() : fixtureDayFour;
  if (world === "main") {
    return {
      ...baseEnvelope,
      projects: baseEnvelope.projects.map((p) => ({ ...p, day: p.code === "SS418" ? day : EMPTY_DAY(FIXTURE_DAY_DATE) })),
    };
  }
  const ss418 = baseEnvelope.projects.find((p) => p.code === "SS418");
  return { ...baseEnvelope, counts: { today: 0, ncrs: 0, reports: 0 }, projects: [{ ...ss418, groups: { reinspectionsOverdue: [], ncrsRecorded: [], resultsMissing: [] }, onSiteToday: [], day }] };
}

/**
 * Fixture-mode writes for End of day (there is no server): returns a new Today
 * envelope with the write applied, as the server's read-back would show it.
 *   { type: "close", projectCode, scheduleRef, close: "carry" | "not_attended", reason?, carriedTo? }
 *   { type: "approve", projectCode, inspectionNumbers: [] }
 */
export function applyEndOfDayWrite(envelope, write) {
  return {
    ...envelope,
    projects: (envelope.projects || []).map((p) => {
      if (!p.day || p.code !== write.projectCode) return p;
      if (write.type === "approve") {
        const keep = (p.day.toApprove || []).filter((r) => !write.inspectionNumbers.includes(r.inspectionNumber));
        return { ...p, day: { ...p.day, toApprove: keep, approvedCount: (p.day.approvedCount || 0) + (p.day.toApprove || []).length - keep.length } };
      }
      const lines = (p.day.lines || []).map((l) => ({
        ...l,
        sides: l.sides.map((s) => {
          if (s.scheduleRef !== write.scheduleRef) return s;
          return write.close === "carry"
            ? { ...s, state: "carried", carriedTo: write.carriedTo || "" }
            : { ...s, state: "not_attended", notAttendedReason: write.reason || "" };
        }),
      }));
      const counts = countsOf(lines, p.day.counts.unscheduled);
      return { ...p, day: { ...p.day, lines, counts } };
    }),
  };
}
